/**
 * Maps a source image filename, as it appears in the CSV data, onto the URLs of
 * the responsive variants that `generateThumbnails()` in `gatsby-node.js` emits
 * at build time.
 *
 * Filenames arrive unprefixed and may include a subfolder, e.g. `foo.jpg` or
 * `vkj/bar.jpg`. Every URL produced here is root-relative, so it resolves the
 * same way regardless of the depth of the page rendering it.
 */

/** Root path under which all processed images are served. */
export const IMAGE_ROOT = '/images';

/** Pixel densities for which thumbnail variants are generated at build time. */
export const IMAGE_DENSITIES = ['1x', '2x', '3x'] as const;

export type ImageDensity = typeof IMAGE_DENSITIES[number];

/** Encodings in which each density is generated. */
export type ImageEncoding = 'webp' | 'jpg';

/** Everything needed to render one responsive image with a fallback chain. */
export interface IImageSources {
  /** `srcset` for the WebP thumbnail variants. */
  webpSrcSet: string;
  /** `srcset` for the JPEG thumbnail variants. */
  jpegSrcSet: string;
  /** Smallest JPEG thumbnail; the default `<img>` src and first fallback. */
  thumbnailUrl: string;
  /** The unprocessed source image; last-resort fallback. */
  originalUrl: string;
}

/**
 * How far an image has degraded through its fallback chain.
 *
 * `sources`   — the `<picture>` sources are in play; the browser picks WebP.
 * `thumbnail` — sources dropped, loading the 1x JPEG thumbnail directly.
 * `original`  — thumbnails unavailable, loading the unprocessed source image.
 */
export type ImageFallbackStage = 'sources' | 'thumbnail' | 'original';

/** Drops the final extension: `a.b.jpg` -> `a.b`, `vkj/x.jpg` -> `vkj/x`. */
export const stripImageExtension = (filename: string): string =>
  filename.replace(/\.[^./]+$/, '');

/**
 * Root-relative URL of one generated variant of an already-stripped base name.
 *
 * Takes the base rather than a filename because `stripImageExtension` is not
 * safe to apply twice: a name like `Untitled-1.5` loses its `.5` on the second
 * pass. Callers holding a filename strip once, then build every variant from
 * the result.
 */
export const variantUrl = (
  base: string,
  suffix: string,
  encoding: ImageEncoding
): string => `${IMAGE_ROOT}/${base}-${suffix}.${encoding}`;

/** Root-relative URL of one generated variant, e.g. `/images/foo-3x.jpg`. */
export const thumbnailPath = (
  filename: string,
  density: ImageDensity,
  encoding: ImageEncoding = 'jpg'
): string => variantUrl(stripImageExtension(filename), density, encoding);

/**
 * Width, in pixels, of the zoom variant — the largest picture the site serves.
 *
 * The sources behind these thumbnails are archival scans: 1054 files, the
 * widest 5833px, the largest 28MB. Serving one of those to the zoom view is a
 * ten-second wait for detail no screen can resolve. 2048 fills a full-screen
 * viewer on a retina display and costs 300KB-1.7MB instead — a 20-80x
 * reduction on exactly the images that were slow.
 *
 * Kept in step with `ZOOM_WIDTH` in `gatsby-node.js`, which writes the files.
 */
export const ZOOM_WIDTH = 2048;

/** Suffix marking the zoom variant, e.g. `/images/foo-zoom.webp`. */
export const ZOOM_SUFFIX = 'zoom';

/**
 * The zoom variant, which every image has: it is written for small sources
 * too, at their own size rather than enlarged, so this URL always resolves and
 * the zoom view needs no manifest of which images got one.
 */
export const zoomPath = (base: string): string =>
  variantUrl(base, ZOOM_SUFFIX, 'webp');

/**
 * Width of the deep variant, loaded only when there is reason to think it will
 * be looked at closely.
 *
 * 2048 covers the fitted view and about the first two stops of zoom on a
 * retina display; past that the reader is enlarging pixels rather than
 * revealing paint. This is the tier that answers that, and it exists only for
 * the 138 sources with more than `ZOOM_WIDTH` to give — the rest are already
 * served whole by the zoom variant, and a second copy of them would be the
 * same bytes under a different name.
 *
 * Kept in step with `DEEP_WIDTH` in `gatsby-node.js`, which writes the files.
 */
export const DEEP_WIDTH = 4096;

/** Suffix marking the deep variant, e.g. `/images/foo-deep.webp`. */
export const DEEP_SUFFIX = 'deep';

/**
 * The deep variant. Unlike `zoomPath` this is *not* guaranteed to resolve: it
 * is written only for sources wider than `ZOOM_WIDTH`. A caller can tell
 * whether one exists without a manifest, by asking whether the zoom variant
 * came back at the full `ZOOM_WIDTH` — that only happens when the source had
 * at least that much to give.
 */
export const deepPath = (base: string): string =>
  variantUrl(base, DEEP_SUFFIX, 'webp');

const ORIGIN = /^[a-z][a-z0-9+.-]*:\/\/[^/]+/i;
const DENSITY_SUFFIX = new RegExp(`-(${IMAGE_DENSITIES.join('|')})$`);

/**
 * Reduces however an image was referenced back to the bare base name its
 * variants are built from.
 *
 * Three callers hand over three different things for the same picture: the CSV
 * gives `1388.jpg`, the hand-written gallery history gives `vkj/Nand-Lal.jpg`,
 * and an essay — where the only handle on the image is the DOM element the
 * reader clicked — gives a resolved `https://host/images/vkj/Nand-Lal-2x.webp`.
 * All three have to name the same variants, so the origin, the `/images` root,
 * any density suffix and the extension all come off.
 */
export const normalizeImageName = (reference: string): string => {
  const path = reference.replace(ORIGIN, '');
  const rooted = path.startsWith(`${IMAGE_ROOT}/`)
    ? path.slice(IMAGE_ROOT.length + 1)
    : path.replace(/^\//, '');
  return stripImageExtension(rooted).replace(DENSITY_SUFFIX, '');
};

const srcSet = (filename: string, encoding: ImageEncoding): string =>
  IMAGE_DENSITIES.map(
    (density) => `${thumbnailPath(filename, density, encoding)} ${density}`
  ).join(', ');

export const buildImageSources = (filename: string): IImageSources => ({
  webpSrcSet: srcSet(filename, 'webp'),
  jpegSrcSet: srcSet(filename, 'jpg'),
  thumbnailUrl: thumbnailPath(filename, '1x', 'jpg'),
  originalUrl: `${IMAGE_ROOT}/${filename}`,
});
